/**
 * Page "Refresh data" — shared by the runtime page and the page editor.
 *
 * POST /tenant/pages/:id/refresh-data runs every widget's chain with forced
 * upstream pulls and returns only after the runs finish, so a big page can
 * take a while. The response names what ran, what was skipped and why, and
 * per-widget warnings; `refreshToastMessages` turns that into the toasts both
 * surfaces show.
 */

import { apiFetch } from "./api.js";

export type RefreshSkip = { source: string; reason: string; widget_title?: string; message?: string };

export type RefreshPageDataResult = {
  started: Array<{ source: string }>;
  skipped: RefreshSkip[];
  warnings?: Array<{ source: string; code: string; message: string }>;
};

export function refreshPageData(pageId: string): Promise<RefreshPageDataResult> {
  return apiFetch<RefreshPageDataResult>(`/api/tenant/pages/${encodeURIComponent(pageId)}/refresh-data`, {
    method: "POST",
    body: {}
  });
}

export function formatSkipToast(skips: RefreshSkip[]): string {
  // Group widgets by their failure message so multiple widgets sharing one
  // root cause render as a single line.
  const groups = new Map<string, string[]>();
  for (const s of skips) {
    const title = s.widget_title?.trim() || "(untitled widget)";
    const detail =
      s.reason === "operation_not_found" ? "the underlying operation was deleted" :
      s.reason === "operation_requires_validation" ? "open the operation in the builder to validate it" :
      s.reason === "operation_has_no_graph" ? "the operation has no graph yet — open it in the builder" :
      s.reason === "invalid_operation_spec" ? "the operation is misconfigured — open it in the builder" :
      s.message?.trim() ||
      "check the operation's execution log";
    const existing = groups.get(detail);
    if (existing) existing.push(title);
    else groups.set(detail, [title]);
  }
  const lines: string[] = [];
  for (const [detail, titles] of groups) {
    const uniq = [...new Set(titles)];
    const subject = uniq.length === 1
      ? `Widget '${uniq[0]}'`
      : `Widgets ${uniq.map((t) => `'${t}'`).join(", ")}`;
    lines.push(`${subject}: ${detail}`);
  }
  return lines.join(" • ");
}

export type RefreshToast = { kind: "error" | "success"; message: string };

/** The toasts a refresh outcome earns: zero-row preservation, authoring-mode AI skips, and hard skips. */
export function refreshToastMessages(result: RefreshPageDataResult): RefreshToast[] {
  const out: RefreshToast[] = [];
  const warnings = result.warnings ?? [];
  const zeroRowWarnings = warnings.filter((w) => w.code === "zero_rows");
  if (zeroRowWarnings.length > 0) {
    out.push({
      kind: "error",
      message: `${zeroRowWarnings.length === 1 ? "A data provider" : "Some data providers"} returned 0 rows — the previous data was preserved. Check your data source for issues.`
    });
  }
  const aiAuthoringSkips = warnings.filter((w) => w.code === "ai_authoring_skip");
  if (aiAuthoringSkips.length > 0) {
    out.push({
      kind: "error",
      message: `${aiAuthoringSkips.length === 1 ? "An AI provider" : "Some AI providers"} did not run — the node is in Authoring mode. Open the operation in the builder and click "Lock for Production" to enable refresh.`
    });
  }
  if (result.skipped.length > 0) {
    out.push({ kind: "error", message: `Refresh failed. ${formatSkipToast(result.skipped)}` });
  }
  return out;
}
